import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { DomSanitizer, SafeResourceUrl, Title, Meta } from '@angular/platform-browser';

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Feature {
  title: string;
  desc: string;
}

interface RubroData {
  title_esp: string;
  title_eng: string;
  subtitle_esp: string;
  subtitle_eng: string;
  intro_esp: string;
  intro_eng: string;
  image: string;
  features_esp: Feature[];
  features_eng: Feature[];
  closing_esp: string;
  closing_eng: string;
}

interface Translations {
  home: string;
  services: string;
  label: string;
  features_label: string;
  cta_label: string;
  cta_title: string;
  cta_whatsapp: string;
  cta_email: string;
}

@Component({
  selector: 'articulo',
  templateUrl: './articulo.Component.html',
  styleUrls: ['./articulo.component.scss'],
})
export class ArticuloComponent implements OnInit {

  // ─── State (igual que tu código original) ─────────────────────────────────
  event: string;
  inmo: number;
  agro: number;
  insti: number;
  arq: number;

  lang: 'ESP' | 'ENG' = 'ESP';
  t: Translations = {} as Translations;

  // ─── Translations ──────────────────────────────────────────────────────────
  private translations: Record<'ESP' | 'ENG', Translations> = {
    ESP: {
      home:           'Inicio',
      services:       'Servicios',
      label:          'Soluciones por rubro',
      features_label: '¿Qué ofrecemos?',
      cta_label:      '¿Listo para empezar?',
      cta_title:      'Trabajemos<br><em>juntos</em>',
      cta_whatsapp:   'Hablar por WhatsApp',
      cta_email:      'Enviar un email',
    },
    ENG: {
      home:           'Home',
      services:       'Services',
      label:          'Industry solutions',
      features_label: 'What we offer',
      cta_label:      'Ready to get started?',
      cta_title:      "Let's work<br><em>together</em>",
      cta_whatsapp:   'Chat on WhatsApp',
      cta_email:      'Send an email',
    },
  };

  // ─── Rubro data ────────────────────────────────────────────────────────────
  private rubros: Record<string, RubroData> = {

    // ── Inmobiliario ──────────────────────────────────────────────────────────
    inmo: {
      title_esp: 'Rubro Inmobiliario',
      title_eng: 'Real Estate',
      subtitle_esp: 'Presencia digital que impulsa la captación de clientes y la gestión de propiedades.',
      subtitle_eng: 'Digital presence that drives client acquisition and property management.',
      intro_esp: 'En <strong>Patagonia Chain</strong>, sabemos que el sector inmobiliario está en constante evolución y que tener una presencia digital sólida es clave para destacar en un mercado tan competitivo. Ofrecemos soluciones personalizadas que no solo facilitan la gestión de propiedades, sino que también maximizan la exposición y captación de nuevos clientes.',
      intro_eng: 'At <strong>Patagonia Chain</strong>, we know the real estate sector is constantly evolving and that a strong digital presence is key to standing out in such a competitive market. We offer tailored solutions that not only streamline property management, but also maximize exposure and new client acquisition.',
      image: '../../assets/inmo.jpeg',
      features_esp: [
        {
          title: 'Páginas web a medida',
          desc: 'Creamos sitios web diseñados específicamente para inmobiliarias, con enfoque en la presentación visual y una experiencia de usuario intuitiva. Ya sea para un emprendimiento nuevo o una empresa establecida, nuestros desarrollos son 100% personalizados para reflejar la identidad y los objetivos de tu negocio.',
        },
        {
          title: 'Integración con portales de búsqueda',
          desc: 'Integramos tu web con portales populares como Mercado Libre, InfoCasas y Casas y Más, permitiendo que las propiedades que publiques en tu sitio estén automáticamente visibles en estas plataformas y lleguen a más clientes potenciales.',
        },
        {
          title: 'Sistemas de búsqueda avanzados',
          desc: 'Implementamos filtros de búsqueda por ubicación, precio, tipo de propiedad y más, para que tus clientes encuentren exactamente lo que buscan de manera rápida y sin fricciones.',
        },
        {
          title: 'Gestión de propiedades en tiempo real',
          desc: 'Te ofrecemos un panel de administración fácil de usar donde podés agregar, actualizar o eliminar propiedades en tiempo real, gestionando tu inventario de manera ágil y sin complicaciones técnicas.',
        },
        {
          title: 'Optimización móvil y SEO',
          desc: 'Todos nuestros desarrollos son completamente responsivos, asegurando una experiencia óptima en cualquier dispositivo. Además, optimizamos tu sitio para que aparezca en los primeros resultados de Google, aumentando la visibilidad de tus propiedades de forma orgánica.',
        },
      ],
      features_eng: [
        {
          title: 'Custom websites',
          desc: 'We create websites designed specifically for real estate agencies, focused on visual presentation and intuitive user experience. Whether for a new venture or an established company, our developments are 100% customized to reflect your brand identity and business goals.',
        },
        {
          title: 'Property portal integration',
          desc: 'We integrate your website with popular portals like Mercado Libre, InfoCasas, and Casas y Más, allowing properties you publish on your site to be automatically visible on these platforms — reaching more potential clients.',
        },
        {
          title: 'Advanced search systems',
          desc: 'We implement search filters by location, price, property type, and more, so your clients can find exactly what they\'re looking for quickly and without friction.',
        },
        {
          title: 'Real-time property management',
          desc: 'We provide an easy-to-use admin panel where you can add, update, or remove properties in real time — managing your inventory efficiently and without technical complications.',
        },
        {
          title: 'Mobile optimization & SEO',
          desc: 'All our developments are fully responsive, ensuring an optimal experience on any device. We also optimize your site to appear in top Google results, increasing your property visibility organically.',
        },
      ],
      closing_esp: '¿Listo para construir una plataforma digital que impulse tu negocio inmobiliario? Contactanos y empecemos a trabajar juntos en la solución ideal para tu inmobiliaria.',
      closing_eng: 'Ready to build a digital platform that drives your real estate business? Contact us and let\'s start working together on the ideal solution for your agency.',
    },

    // ── Agro ──────────────────────────────────────────────────────────────────
    agro: {
      title_esp: 'Industria del Agro',
      title_eng: 'Agricultural Industry',
      subtitle_esp: 'Tecnología a medida para optimizar la gestión y productividad del campo.',
      subtitle_eng: 'Tailored technology to optimize agricultural management and productivity.',
      intro_esp: 'En <strong>Patagonia Chain</strong>, entendemos los desafíos únicos que enfrenta la industria agrícola y cómo la tecnología puede ser un aliado fundamental para mejorar la eficiencia y productividad. Desarrollamos soluciones tecnológicas a medida que permiten a las empresas agrícolas optimizar su gestión, tomar mejores decisiones y mejorar sus resultados.',
      intro_eng: 'At <strong>Patagonia Chain</strong>, we understand the unique challenges faced by the agricultural industry and how technology can be a fundamental ally for improving efficiency and productivity. We develop tailored tech solutions that allow agricultural companies to optimize their management, make better decisions, and improve their results.',
      image: '../../assets/agro.png',
      features_esp: [
        {
          title: 'Sistemas de gestión agrícola personalizados',
          desc: 'Desarrollamos plataformas para la gestión de cultivos, maquinaria, personal y otras operaciones clave. Esto permite un control eficiente de los recursos y mejora la toma de decisiones basada en datos reales y actualizados.',
        },
        {
          title: 'Páginas web y tiendas online',
          desc: 'Si tu empresa vende maquinaria, fertilizantes u otros productos agrícolas, creamos páginas web optimizadas y tiendas online que te permiten vender directamente a tus clientes, facilitando el acceso a tus servicios desde cualquier dispositivo.',
        },
        {
          title: 'Gestión de cartera de clientes (CRM)',
          desc: 'Implementamos sistemas CRM que permiten gestionar relaciones con clientes, realizar seguimiento de pedidos, automatizar comunicaciones y analizar el comportamiento de compra para mejorar la atención y aumentar las ventas.',
        },
        {
          title: 'Gestión de inventario y stock',
          desc: 'Diseñamos plataformas que facilitan el control del inventario de maquinaria, insumos y productos. Estas herramientas permiten prever necesidades de reabastecimiento y optimizar la logística para reducir costos y mejorar la eficiencia operativa.',
        },
        {
          title: 'Automatización de procesos',
          desc: 'Implementamos sistemas que automatizan procesos repetitivos como la planificación de riego, fertilización o seguimiento del inventario, permitiendo a los productores ahorrar tiempo, reducir errores y enfocarse en lo que realmente importa.',
        },
      ],
      features_eng: [
        {
          title: 'Custom agricultural management systems',
          desc: 'We develop platforms for managing crops, machinery, personnel, and other key operations. This enables efficient resource control and improves decision-making based on real, up-to-date data.',
        },
        {
          title: 'Websites and online stores',
          desc: 'If your company sells machinery, fertilizers, or other agricultural products, we create optimized websites and online stores that let you sell directly to your customers from any device.',
        },
        {
          title: 'Client portfolio management (CRM)',
          desc: 'We implement CRM systems to manage client relationships, track orders, automate communications, and analyze purchasing behavior — improving customer service and increasing sales.',
        },
        {
          title: 'Inventory and stock management',
          desc: 'We design platforms that facilitate the control of machinery, inputs, and product inventories — allowing you to forecast restocking needs and optimize logistics to reduce costs and improve operational efficiency.',
        },
        {
          title: 'Process automation',
          desc: 'We implement systems that automate repetitive processes such as irrigation planning, fertilization, or inventory tracking — allowing producers to save time, reduce errors, and focus on what truly matters.',
        },
      ],
      closing_esp: 'En Patagonia Chain, no solo desarrollamos software; creamos herramientas que ayudan a los productores a tomar mejores decisiones y mantenerse competitivos en un mercado cada vez más exigente. Contactanos y optimicemos juntos la gestión de tu empresa agrícola.',
      closing_eng: 'At Patagonia Chain, we don\'t just develop software — we create tools that help producers make better decisions and stay competitive in an increasingly demanding market. Contact us and let\'s optimize your agricultural business together.',
    },

    // ── Arquitectura ──────────────────────────────────────────────────────────
    arq: {
      title_esp: 'Estudios de Arquitectura',
      title_eng: 'Architecture Studios',
      subtitle_esp: 'Herramientas digitales que potencian la gestión de proyectos y la presencia de tu estudio.',
      subtitle_eng: 'Digital tools that enhance project management and your studio\'s online presence.',
      intro_esp: 'En <strong>Patagonia Chain</strong>, ofrecemos soluciones digitales personalizadas que potencian la eficiencia y productividad de los estudios de arquitectura. Comprendemos las necesidades únicas del sector y desarrollamos herramientas que optimizan la gestión de proyectos, clientes y recursos, permitiendo a los arquitectos centrarse en lo que mejor saben hacer: crear y diseñar.',
      intro_eng: 'At <strong>Patagonia Chain</strong>, we offer personalized digital solutions that enhance the efficiency and productivity of architecture studios. We understand the unique needs of the sector and develop tools that optimize project, client, and resource management — allowing architects to focus on what they do best: creating and designing.',
      image: '../../assets/arquitectura.jpeg',
      features_esp: [
        {
          title: 'Sitios web profesionales',
          desc: 'Creamos páginas web modernas y atractivas que destacan los proyectos de tu estudio y facilitan la presentación de tu portfolio y servicios. Nos enfocamos en crear una presencia digital que refleje tu estilo y la calidad de tus trabajos.',
        },
        {
          title: 'Gestión de proyectos arquitectónicos',
          desc: 'Desarrollamos software para organizar y hacer seguimiento de proyectos, desde la planificación hasta la entrega final. Estas plataformas permiten gestionar cronogramas, recursos, equipos y presupuestos de manera eficiente y centralizada.',
        },
        {
          title: 'Gestión de clientes y comunicación (CRM)',
          desc: 'Implementamos sistemas CRM para gestionar la relación con los clientes, organizar reuniones, registrar el progreso de proyectos y automatizar la comunicación, mejorando la experiencia del cliente y asegurando un flujo de información fluido.',
        },
        {
          title: 'Control de presupuestos y recursos',
          desc: 'Nuestras soluciones permiten gestionar los costos de cada proyecto, el uso de materiales y los recursos humanos, ayudando a optimizar los márgenes de beneficio y evitar sobrecostos no planificados.',
        },
        {
          title: 'Portales interactivos para clientes',
          desc: 'Ofrecemos portales donde tus clientes pueden consultar el progreso de sus proyectos, descargar planos y documentos, y revisar las etapas de construcción, facilitando la transparencia y fortaleciendo la relación cliente-estudio.',
        },
      ],
      features_eng: [
        {
          title: 'Professional websites',
          desc: 'We create modern, attractive websites that showcase your studio\'s projects and facilitate the presentation of your portfolio and services — reflecting your style and the quality of your work.',
        },
        {
          title: 'Architectural project management',
          desc: 'We develop software to organize and track projects from planning to final delivery. These platforms allow efficient management of schedules, resources, teams, and budgets in a centralized way.',
        },
        {
          title: 'Client management & communication (CRM)',
          desc: 'We implement CRM systems to manage client relationships, organize meetings, track project progress, and automate communication — improving client experience and ensuring a smooth information flow.',
        },
        {
          title: 'Budget and resource control',
          desc: 'Our solutions allow you to manage project costs, material usage, and human resources — helping optimize profit margins and avoid unplanned cost overruns.',
        },
        {
          title: 'Interactive client portals',
          desc: 'We offer portals where your clients can check project progress, download plans and documents, and review construction stages — promoting transparency and strengthening the client-studio relationship.',
        },
      ],
      closing_esp: 'En Patagonia Chain, diseñamos soluciones tecnológicas que crecen junto a tu estudio. Si deseás optimizar tu gestión interna o mejorar tu presencia digital, estamos listos para ayudarte.',
      closing_eng: 'At Patagonia Chain, we design technology solutions that grow alongside your studio. If you want to optimize your internal management or improve your digital presence, we\'re ready to help.',
    },

    // ── Instituciones ─────────────────────────────────────────────────────────
    insti: {
      title_esp: 'Instituciones',
      title_eng: 'Institutions',
      subtitle_esp: 'Soluciones digitales que simplifican la gestión y mejoran la experiencia de usuarios y miembros.',
      subtitle_eng: 'Digital solutions that simplify management and improve the experience of users and members.',
      intro_esp: 'En <strong>Patagonia Chain</strong>, estamos comprometidos con ofrecer soluciones digitales que optimicen los procesos y mejoren la eficiencia de todo tipo de instituciones: desde organizaciones educativas, de salud y gubernamentales hasta entidades sin fines de lucro. Comprendemos las necesidades únicas de cada institución y creamos herramientas que simplifican la gestión interna y promueven una mayor productividad.',
      intro_eng: 'At <strong>Patagonia Chain</strong>, we are committed to delivering digital solutions that optimize processes and improve efficiency for all types of institutions — from educational, health, and government organizations to non-profit entities. We understand each institution\'s unique needs and create tools that simplify internal management and promote greater productivity.',
      image: '../../assets/institucion.jpeg',
      features_esp: [
        {
          title: 'Plataformas web institucionales',
          desc: 'Diseñamos y desarrollamos páginas web profesionales que facilitan la comunicación con el público, la presentación de servicios y actividades, y la creación de canales eficientes de interacción con usuarios, padres, alumnos, miembros o ciudadanos.',
        },
        {
          title: 'Gestión de usuarios y recursos',
          desc: 'Implementamos software de gestión para el control de usuarios, recursos y operaciones internas, adaptado a las necesidades específicas de cada institución: personal, alumnos, socios o cualquier tipo de registro que tu organización requiera.',
        },
        {
          title: 'Automatización de procesos administrativos',
          desc: 'Nuestras soluciones permiten automatizar procesos repetitivos como la inscripción de estudiantes, el seguimiento de pagos, el manejo de expedientes o la asignación de recursos, reduciendo errores humanos y mejorando la eficiencia operativa.',
        },
        {
          title: 'Gestión documental y archivo digital',
          desc: 'Ofrecemos sistemas para el almacenamiento seguro y organizado de documentos importantes: archivos académicos y administrativos, registros históricos y más. Nuestras soluciones facilitan el acceso rápido y eficiente a la información cuando más la necesitás.',
        },
        {
          title: 'Portales interactivos para usuarios',
          desc: 'Creamos portales donde los usuarios pueden realizar gestiones de manera autónoma: consultar notas, pagar servicios, descargar documentos o registrar solicitudes, mejorando significativamente la experiencia del usuario y reduciendo la carga administrativa.',
        },
      ],
      features_eng: [
        {
          title: 'Institutional web platforms',
          desc: 'We design and develop professional websites that facilitate public communication, service and activity presentation, and the creation of efficient interaction channels with users, parents, students, members, or citizens.',
        },
        {
          title: 'User and resource management',
          desc: 'We implement management software for controlling users, resources, and internal operations — tailored to each institution\'s specific needs, whether for staff, students, members, or any type of record your organization requires.',
        },
        {
          title: 'Administrative process automation',
          desc: 'Our solutions allow institutions to automate repetitive processes such as student enrollment, payment tracking, file management, or resource allocation — reducing human error and improving operational efficiency.',
        },
        {
          title: 'Document management & digital archive',
          desc: 'We offer systems for the secure and organized storage of important documents: academic and administrative files, historical records, and more. Our solutions provide quick, efficient access to information whenever you need it.',
        },
        {
          title: 'Interactive user portals',
          desc: 'We create portals where users can self-manage: check grades, pay for services, download documents, or submit requests — significantly improving user experience and reducing administrative workload.',
        },
      ],
      closing_esp: 'Ya sea que tu institución esté en pleno crecimiento o necesite actualizar sus sistemas, en Patagonia Chain diseñamos soluciones escalables adaptadas al tamaño y necesidades de tu organización. Contactanos y trabajemos juntos en una solución tecnológica a medida.',
      closing_eng: 'Whether your institution is in full growth or needs to update its systems, at Patagonia Chain we design scalable solutions adapted to your organization\'s size and needs. Contact us and let\'s work together on a custom technology solution.',
    },
  };

  // ─── Constructor (igual al tuyo original) ──────────────────────────────────
  constructor(
    private authService: AuthService,
    private domSanitizer: DomSanitizer,
    public router: Router,
    private title: Title,
    private meta: Meta,
    private rutaActiva: ActivatedRoute,
  ) {}

  // ─── ngOnInit (idéntico al original + lógica nueva) ────────────────────────
  ngOnInit(): void {
    this.event = this.rutaActiva.snapshot.params['id'];

    // Idioma desde sessionStorage
    const savedLang = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null;
    this.lang = savedLang ?? 'ESP';
    this.t = this.translations[this.lang];

    // Flags y SEO — idénticos a tu código original
    if (this.event === 'rubro-inmobiliario') {
      this.inmo = 1;
      this.title.setTitle('Desarrollo Web Inmobiliario | Gestión de Propiedades Online | Patagonia Chain');
      this.meta.addTag({ name: 'title',       content: 'Desarrollo Web Inmobiliario | Gestión de Propiedades | Landing Pages para Inmobiliarias | Patagonia Chain' });
      this.meta.addTag({ name: 'description', content: 'Desarrollo web personalizado para inmobiliarias | Gestión de propiedades en línea | Páginas web optimizadas para ventas de inmuebles | Soluciones digitales para el sector inmobiliario | Patagonia Chain' });
    }

    if (this.event === 'rubro-agro') {
      this.agro = 1;
      this.title.setTitle('Desarrollo Web para el Agro | Soluciones Digitales Agrícolas | Patagonia Chain');
      this.meta.addTag({ name: 'title',       content: 'Desarrollo Web Agro | Gestión de Clientes y Stock | Tiendas Online para el Agro | Patagonia Chain' });
      this.meta.addTag({ name: 'description', content: 'Desarrollo web personalizado para el sector agro | Soluciones de gestión de clientes y stock | Páginas web y tiendas online para empresas agrícolas | Patagonia Chain' });
    }

    if (this.event === 'instituciones') {
      this.insti = 1;
      this.title.setTitle('Desarrollo Web y Software de Gestión para Instituciones | Patagonia Chain');
      this.meta.addTag({ name: 'title',       content: 'Desarrollo Web Institucional | Software de Gestión para Organizaciones | Patagonia Chain' });
      this.meta.addTag({ name: 'description', content: 'Desarrollo web y software de gestión a medida para instituciones | Soluciones tecnológicas para organizaciones educativas, gubernamentales y sin fines de lucro | Patagonia Chain' });
    }

    if (this.event === 'estudio-arquitectura') {
      this.arq = 1;
      this.title.setTitle('Desarrollo Web y Software de Gestión para Arquitectos | Patagonia Chain');
      this.meta.addTag({ name: 'title',       content: 'Desarrollo Web para Estudios de Arquitectura | Gestión de Proyectos Arquitectónicos | Patagonia Chain' });
      this.meta.addTag({ name: 'description', content: 'Desarrollo web y software de gestión para estudios de arquitectura | Optimización de proyectos arquitectónicos | Gestión de clientes, recursos y presupuestos | Patagonia Chain' });
    }

    window.scroll(0, 0);
  }

  // ─── Detecta cambios de idioma en tiempo real ──────────────────────────────
  ngDoCheck(): void {
    try {
      const current = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null;
      const next = current ?? 'ESP';
      if (next !== this.lang) {
        this.lang = next;
        this.t = this.translations[this.lang];
      }
    } catch {}
  }

  // ─── Helpers para el template ──────────────────────────────────────────────
  private getCurrentRubro(): RubroData | null {
    if (this.inmo)  return this.rubros['inmo'];
    if (this.agro)  return this.rubros['agro'];
    if (this.arq)   return this.rubros['arq'];
    if (this.insti) return this.rubros['insti'];
    return null;
  }

  getCurrentTitle(): string {
    const r = this.getCurrentRubro();
    if (!r) return '';
    return this.lang === 'ESP' ? r.title_esp : r.title_eng;
  }

  getCurrentSubtitle(): string {
    const r = this.getCurrentRubro();
    if (!r) return '';
    return this.lang === 'ESP' ? r.subtitle_esp : r.subtitle_eng;
  }

  getCurrentIntro(): string {
    const r = this.getCurrentRubro();
    if (!r) return '';
    return this.lang === 'ESP' ? r.intro_esp : r.intro_eng;
  }

  getCurrentImage(): string {
    return this.getCurrentRubro()?.image ?? '';
  }

  getCurrentFeatures(): Feature[] {
    const r = this.getCurrentRubro();
    if (!r) return [];
    return this.lang === 'ESP' ? r.features_esp : r.features_eng;
  }

  getClosingText(): string {
    const r = this.getCurrentRubro();
    if (!r) return '';
    return this.lang === 'ESP' ? r.closing_esp : r.closing_eng;
  }
}
